/**
 * Account Utilities
 * Functions for managing account calculations and operations
 */

import { getInvestmentMarketValue } from './netWorthCalculations';

/**
 * Calculate monthly contribution amount from account settings
 * @param {Object} account - Account object with contributionAmount and contributionFrequency
 * @returns {number} - Monthly contribution amount
 */
export const calculateMonthlyContribution = (account) => {
  if (!account || !account.contributionAmount) {
    return 0;
  }

  const amount = parseFloat(account.contributionAmount) || 0;
  const frequency = account.contributionFrequency || 'monthly';

  switch (frequency.toLowerCase()) {
    case 'monthly':
      return amount;
    case 'quarterly':
      return amount / 3;
    case 'annual':
      return amount / 12;
    default:
      return amount; // Default to monthly if unknown frequency
  }
};

/**
 * Calculate annual contribution amount from account settings
 * @param {Object} account - Account object with contributionAmount and contributionFrequency
 * @returns {number} - Annual contribution amount
 */
export const calculateAnnualContribution = (account) => {
  if (!account || !account.contributionAmount) {
    return 0;
  }

  const amount = parseFloat(account.contributionAmount) || 0;
  const frequency = account.contributionFrequency || 'monthly';

  switch (frequency.toLowerCase()) {
    case 'monthly':
      return amount * 12;
    case 'quarterly':
      return amount * 4;
    case 'annual':
      return amount;
    default:
      return amount * 12; // Default to monthly if unknown frequency
  }
};

/**
 * Calculate the balance of an account from investments held in it
 * Uses live market values when priceMap provided, falls back to cost basis
 *
 * @param {string} accountId - The account ID
 * @param {Array} investments - Array of investment objects
 * @param {string} displayCurrency - Display currency code (e.g., 'USD', 'GBP')
 * @param {Object} exchangeRates - Exchange rates object with rates relative to EUR
 * @param {Object} priceMap - Optional map of ticker → { price, currency, ... } from live pricing
 * @returns {number} - Total balance in the account (converted to display currency if rates available)
 */
export const calculateAccountBalance = (accountId, investments = [], displayCurrency = null, exchangeRates = null, priceMap = null) => {
  if (!accountId || !Array.isArray(investments)) {
    return 0;
  }

  const accountInvestments = investments.filter(inv => inv.accountId === accountId);

  // If no currency conversion needed or rates not available
  if (!exchangeRates || !exchangeRates.rates || !displayCurrency) {
    return accountInvestments.reduce((sum, inv) => sum + getInvestmentMarketValue(inv, priceMap), 0);
  }

  // Calculate with currency conversion using market values
  return accountInvestments.reduce((sum, inv) => {
    const marketValue = getInvestmentMarketValue(inv, priceMap);
    const investmentCurrency = inv.currency || displayCurrency;

    if (investmentCurrency === displayCurrency) {
      return sum + marketValue;
    }

    const fromRate = exchangeRates.rates[investmentCurrency];
    const toRate = exchangeRates.rates[displayCurrency];

    if (fromRate && toRate) {
      const convertedAmount = (marketValue / fromRate) * toRate;
      return sum + convertedAmount;
    }

    return sum + marketValue; // Fallback
  }, 0);
};

/**
 * Get all investments held in a specific account
 * @param {string} accountId - The account ID
 * @param {Array} investments - Array of investment objects
 * @returns {Array} - Investments held in this account
 */
export const getAccountInvestments = (accountId, investments = []) => {
  if (!accountId || !Array.isArray(investments)) {
    return [];
  }

  return investments.filter(inv => inv.accountId === accountId);
};

/**
 * Calculate total value across all retirement accounts
 * Uses market values when available
 *
 * @param {Array} accounts - Array of account objects
 * @param {Array} investments - Array of investment objects
 * @returns {number} - Total retirement savings (market value)
 */
export const calculateRetirementSavings = (accounts = [], investments = []) => {
  if (!Array.isArray(accounts) || !Array.isArray(investments)) {
    return 0;
  }

  // Get all retirement account IDs
  const retirementAccountIds = accounts
    .filter(acc => acc.accountPurpose === 'retirement')
    .map(acc => acc.id);

  // Sum investments in retirement accounts using market values
  return investments
    .filter(inv => retirementAccountIds.includes(inv.accountId))
    .reduce((sum, inv) => sum + getInvestmentMarketValue(inv), 0);
};

/**
 * Calculate total value across all taxable accounts
 * Uses market values when available
 *
 * @param {Array} accounts - Array of account objects
 * @param {Array} investments - Array of investment objects
 * @returns {number} - Total taxable savings (market value)
 */
export const calculateTaxableSavings = (accounts = [], investments = []) => {
  if (!Array.isArray(accounts) || !Array.isArray(investments)) {
    return 0;
  }

  // Get all taxable account IDs
  const taxableAccountIds = accounts
    .filter(acc => acc.accountPurpose === 'taxable')
    .map(acc => acc.id);

  // Sum investments in taxable accounts using market values
  return investments
    .filter(inv => taxableAccountIds.includes(inv.accountId))
    .reduce((sum, inv) => sum + getInvestmentMarketValue(inv), 0);
};

/**
 * Calculate total value across all crypto accounts
 * Uses market values when available
 *
 * @param {Array} accounts - Array of account objects
 * @param {Array} investments - Array of investment objects
 * @returns {number} - Total crypto holdings (market value)
 */
export const calculateCryptoHoldings = (accounts = [], investments = []) => {
  if (!Array.isArray(accounts) || !Array.isArray(investments)) {
    return 0;
  }

  // Get all crypto account IDs
  const cryptoAccountIds = accounts
    .filter(acc => acc.accountPurpose === 'crypto')
    .map(acc => acc.id);

  // Sum investments in crypto accounts using market values
  return investments
    .filter(inv => cryptoAccountIds.includes(inv.accountId))
    .reduce((sum, inv) => sum + getInvestmentMarketValue(inv), 0);
};

/**
 * Get account breakdown with calculated balances and holdings
 * Uses live market values when priceMap provided
 *
 * @param {Array} accounts - Array of account objects
 * @param {Array} investments - Array of investment objects
 * @param {string} displayCurrency - Display currency code (e.g., 'USD', 'GBP')
 * @param {Object} exchangeRates - Exchange rates object with rates relative to EUR
 * @param {Object} priceMap - Optional map of ticker → { price, currency, ... } from live pricing
 * @returns {Array} - Accounts with calculated balances and holdings
 */
export const getAccountsWithBalances = (accounts = [], investments = [], displayCurrency = null, exchangeRates = null, priceMap = null) => {
  if (!Array.isArray(accounts) || !Array.isArray(investments)) {
    return [];
  }

  return accounts.map(account => {
    const accountInvestments = getAccountInvestments(account.id, investments);
    const calculatedBalance = calculateAccountBalance(account.id, investments, displayCurrency, exchangeRates, priceMap);

    return {
      ...account,
      calculatedBalance,
      holdings: accountInvestments,
      holdingsCount: accountInvestments.length
    };
  });
};

/**
 * Group investments by account
 * Uses live market values when priceMap provided
 *
 * @param {Array} investments - Array of investment objects
 * @param {Array} accounts - Array of account objects
 * @param {string} displayCurrency - Display currency code (e.g., 'USD', 'GBP')
 * @param {Object} exchangeRates - Exchange rates object with rates relative to EUR
 * @param {Object} priceMap - Optional map of ticker → { price, currency, ... } from live pricing
 * @returns {Object} - Investments grouped by account with market values
 */
export const groupInvestmentsByAccount = (investments = [], accounts = [], displayCurrency = null, exchangeRates = null, priceMap = null) => {
  if (!Array.isArray(investments) || !Array.isArray(accounts)) {
    return {};
  }

  const grouped = {
    unassigned: investments.filter(inv => !inv.accountId)
  };

  accounts.forEach(account => {
    const accountInvestments = investments.filter(inv => inv.accountId === account.id);
    if (accountInvestments.length > 0) {
      // Calculate total value using market values with currency conversion if rates available
      let totalValue = 0;
      if (exchangeRates && exchangeRates.rates && displayCurrency) {
        totalValue = accountInvestments.reduce((sum, inv) => {
          const marketValue = getInvestmentMarketValue(inv, priceMap);
          const investmentCurrency = inv.currency || displayCurrency;

          // Convert to display currency
          if (investmentCurrency === displayCurrency) {
            return sum + marketValue;
          }

          const fromRate = exchangeRates.rates[investmentCurrency];
          const toRate = exchangeRates.rates[displayCurrency];

          if (fromRate && toRate) {
            const convertedAmount = (marketValue / fromRate) * toRate;
            return sum + convertedAmount;
          }

          return sum + marketValue; // Fallback if rates not available
        }, 0);
      } else {
        // No conversion - just sum market values
        totalValue = accountInvestments.reduce((sum, inv) => sum + getInvestmentMarketValue(inv, priceMap), 0);
      }

      grouped[account.id] = {
        account,
        investments: accountInvestments,
        totalValue
      };
    }
  });

  return grouped;
};

/**
 * Group investments by asset category
 * Uses live market values when priceMap provided
 *
 * @param {Array} investments - Array of investment objects
 * @param {string} displayCurrency - Display currency code (e.g., 'USD', 'GBP')
 * @param {Object} exchangeRates - Exchange rates object with rates relative to EUR
 * @param {Object} priceMap - Optional map of ticker → { price, currency, ... } from live pricing
 * @returns {Object} - Investments grouped by category with market values
 */
export const groupInvestmentsByCategory = (investments = [], displayCurrency = null, exchangeRates = null, priceMap = null) => {
  if (!Array.isArray(investments)) {
    return {};
  }

  const grouped = {};

  investments.forEach(inv => {
    const category = inv.category || 'Other';
    if (!grouped[category]) {
      grouped[category] = {
        category,
        investments: [],
        totalValue: 0
      };
    }
    grouped[category].investments.push(inv);

    // Add to total value using market values with currency conversion if rates available
    const marketValue = getInvestmentMarketValue(inv, priceMap);
    if (exchangeRates && exchangeRates.rates && displayCurrency) {
      const investmentCurrency = inv.currency || displayCurrency;

      if (investmentCurrency === displayCurrency) {
        grouped[category].totalValue += marketValue;
      } else {
        const fromRate = exchangeRates.rates[investmentCurrency];
        const toRate = exchangeRates.rates[displayCurrency];

        if (fromRate && toRate) {
          const convertedAmount = (marketValue / fromRate) * toRate;
          grouped[category].totalValue += convertedAmount;
        } else {
          grouped[category].totalValue += marketValue; // Fallback
        }
      }
    } else {
      // No conversion - just add market value
      grouped[category].totalValue += marketValue;
    }
  });

  return grouped;
};
